import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "universal-cookie";
import { supabase } from "../supabase-client";


export default function Navbar() {
   const cookies = new Cookies();
   const navigate = useNavigate();
   const [totalProductCart, setTotalProductCart] = useState(0);
   const [logeado, setLogeado] = useState(false);
   const [userName, setUserName] = useState("");
   const [userLoading, setUserLoading] = useState(false);
   const [isOpen, setIsOpen] = useState(false);
   const toggleMenu = () => setIsOpen(!isOpen);

   // Function to fetch user data
   const fetchUserData = async (clave) => {
      setUserLoading(true);
      try {
         if (clave) {
            const { data, error } = await supabase
               .from("usuarios")
               .select("nombre, email")
               .eq("password_hash", clave)
               .single();

            if (error) {
               console.error("Error fetching user data:", error);
               setUserName("Usuario");
            } else if (data) {
               setUserName(data.nombre || data.email.split("@")[0]);
               console.log("✅ User data loaded:", data.nombre);
            }
         }
      } catch (error) {
         console.error("Error fetching user:", error);
         setUserName("Usuario");
      } finally {
         setUserLoading(false);
      }
   };

   // 👈 Check authentication status
   const checkAuthStatus = () => {
      const token = localStorage.getItem("authjwt");

      if (token) {
         setLogeado(true);
         fetchUserData(token); // Use token as user ID
      } else {
         setLogeado(false);
         setUserName("");
      }
   };

   // Handle logout function
   const handleLogout = () => {
      localStorage.removeItem("authjwt");
      cookies.remove("obra", { path: "/" });
      setLogeado(false);
      setUserName("");

      // 👈 Dispatch event to update other components
      window.dispatchEvent(new Event("authUpdated"));

      console.log("✅ Usuario deslogueado");
      navigate("/");
   };

   useEffect(() => {
      // 👈 Initial auth check
      checkAuthStatus();

      // Function to update cart count
      const updateCartCount = () => {
         const cart = cookies.get("obra") || [];
         setTotalProductCart(Array.isArray(cart) ? cart.length : 0);
      };

      // 👈 Listen for auth updates
      const handleAuthUpdate = () => {
         console.log("🔄 Auth state changed, updating navbar...");
         checkAuthStatus();
      };

      // Initial load
      updateCartCount();

      // Add event listeners
      window.addEventListener("cartUpdated", updateCartCount);
      window.addEventListener("authUpdated", handleAuthUpdate); // 👈 New listener

      // Cleanup
      return () => {
         window.removeEventListener("cartUpdated", updateCartCount);
         window.removeEventListener("authUpdated", handleAuthUpdate); // 👈 Cleanup
      };
   }, []);

   return (
      <nav className="bg-white/95 backdrop-blur-sm border-b border-[#EDE9E4] sticky top-0 z-50">
         <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex justify-between h-[68px] items-center">
               {/* Brand */}
               <Link
                  to="/"
                  className="font-display text-[1.55rem] font-medium tracking-wide text-[#1A1A1A] hover:text-[#9B6D3F] transition-colors duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
               >
                  Diego Curutchet
               </Link>

               {/* Desktop Nav */}
               <div className="hidden md:flex items-center gap-7">
                  {[
                     { to: "/", label: "Inicio" },
                     { to: "/muestras", label: "Arte Sacro" },
                     { to: "/obras", label: "Obras Contemporáneas" },
                     { to: "/cotizacion", label: "Cotización" },
                     { to: "/contacto", label: "Contacto" },
                  ].map(({ to, label }) => (
                     <Link
                        key={to}
                        to={to}
                        className="relative text-[0.72rem] uppercase tracking-[0.14em] text-[#7A7570] hover:text-[#1A1A1A] transition-colors duration-300 group py-1"
                     >
                        {label}
                        <span className="absolute -bottom-0 left-0 w-0 h-[1px] bg-[#9B6D3F] group-hover:w-full transition-all duration-300" />
                     </Link>
                  ))}

                  {/* Cart */}
                  <a href="/carrito" className="relative text-[#7A7570] hover:text-[#1A1A1A] transition-colors duration-300">
                     <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17,18C15.89,18 15,18.89 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20C19,18.89 18.1,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5.5C20.95,5.34 21,5.17 21,5A1,1 0 0,0 20,4H5.21L4.27,2M7,18C5.89,18 5,18.89 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20C9,18.89 8.1,18 7,18Z" />
                     </svg>
                     {totalProductCart > 0 && (
                        <span className="absolute -top-2 -right-2 bg-[#9B6D3F] text-white text-[9px] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
                           {totalProductCart}
                        </span>
                     )}
                  </a>

                  {/* Auth */}
                  {logeado ? (
                     <div className="flex items-center gap-5">
                        <span className="text-xs text-[#7A7570]">
                           {userLoading ? (
                              <span className="inline-block w-16 h-2.5 bg-[#EDE9E4] rounded animate-pulse" />
                           ) : (
                              `Hola, ${userName}`
                           )}
                        </span>
                        <Link
                           to="/mispedidos"
                           className="text-[0.72rem] uppercase tracking-[0.14em] text-[#7A7570] hover:text-[#1A1A1A] transition-colors duration-300"
                        >
                           Pedidos
                        </Link>
                        <button
                           onClick={handleLogout}
                           className="text-[0.72rem] uppercase tracking-[0.14em] text-[#7A7570] hover:text-[#9B6D3F] transition-colors duration-300 cursor-pointer"
                        >
                           Salir
                        </button>
                     </div>
                  ) : (
                     <Link
                        to="/login"
                        className="text-[0.72rem] uppercase tracking-[0.14em] text-[#9B6D3F] border border-[#9B6D3F] px-4 py-1.5 hover:bg-[#9B6D3F] hover:text-white transition-colors duration-300"
                     >
                        Ingresar
                     </Link>
                  )}
               </div>

               {/* Mobile button */}
               <div className="md:hidden flex items-center">
                  <button
                     onClick={toggleMenu}
                     className="p-2 text-[#7A7570] hover:text-[#1A1A1A] transition-colors"
                  >
                     {isOpen ? <X size={22} /> : <Menu size={22} />}
                     </button>
               </div>
            </div>
         </div>

         {/* Mobile Menu */}
         <AnimatePresence>
            {isOpen && (
               <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="md:hidden bg-white border-t border-[#EDE9E4] overflow-hidden"
               >
                  <div className="px-6 py-5 space-y-1">
                     {logeado && (
                        <div className="flex items-center gap-2 pb-4 mb-2 border-b border-[#EDE9E4]">
                           <User className="w-4 h-4 text-[#9B6D3F]" />
                           <span className="text-sm text-[#7A7570]">
                              {userLoading ? "..." : `Hola, ${userName}`}
                           </span>
                        </div>
                     )}

                     {[
                        { to: "/", label: "Inicio" },
                        { to: "/muestras", label: "Arte Sacro" },
                        { to: "/obras", label: "Obras Contemporáneas" },
                        { to: "/cotizacion", label: "Cotización" },
                        { to: "/contacto", label: "Contacto" },
                     ].map(({ to, label }) => (
                        <Link
                           key={to}
                           to={to}
                           onClick={toggleMenu}
                           className="block py-2.5 text-[0.78rem] uppercase tracking-[0.12em] text-[#1A1A1A] hover:text-[#9B6D3F] transition-colors"
                        >
                           {label}
                        </Link>
                     ))}

                     {/* Mobile Cart */}
                     <Link
                        to="/carrito"
                        onClick={toggleMenu}
                        className="flex items-center gap-2 py-2.5 text-[0.78rem] uppercase tracking-[0.12em] text-[#1A1A1A] hover:text-[#9B6D3F] transition-colors border-t border-[#EDE9E4] mt-2 pt-3"
                     >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                           <path d="M17,18C15.89,18 15,18.89 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20C19,18.89 18.1,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5.5C20.95,5.34 21,5.17 21,5A1,1 0 0,0 20,4H5.21L4.27,2M7,18C5.89,18 5,18.89 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20C9,18.89 8.1,18 7,18Z" />
                        </svg>
                        Carrito
                        {totalProductCart > 0 && (
                           <span className="ml-1 bg-[#9B6D3F] text-white text-[8px] font-semibold px-1.5 py-0.5 rounded-full">
                              {totalProductCart}
                           </span>
                        )}
                     </Link>

                     {logeado ? (
                        <>
                           <Link
                              to="/mispedidos"
                              onClick={toggleMenu}
                              className="block py-2.5 text-[0.78rem] uppercase tracking-[0.12em] text-[#1A1A1A] hover:text-[#9B6D3F] transition-colors"
                           >
                              Mis Pedidos
                           </Link>
                           <button
                              onClick={handleLogout}
                              className="block py-2.5 text-[0.78rem] uppercase tracking-[0.12em] text-[#7A7570] hover:text-[#9B6D3F] transition-colors cursor-pointer"
                           >
                              Cerrar Sesión
                           </button>
                        </>
                     ) : (
                        <Link
                           to="/login"
                           onClick={toggleMenu}
                           className="block py-2.5 text-[0.78rem] uppercase tracking-[0.12em] text-[#9B6D3F]"
                        >
                           Ingresar
                        </Link>
                     )}
                  </div>
               </motion.div>
            )}
         </AnimatePresence>
      </nav>
   );
}
